import moment from "moment";

// Filtros compartidos entre los tabs de reportes (Resultados, Ventas, Gastos).
// Cualquier filtro que tenga que aplicarse de forma transversal entre los tabs
// vive acá. Hoy: el mes de referencia del calendario y el rango de período.

// Rangos que forzan una fecha-fin canónica distinta del endMonth actual.
// Cuando el caller selecciona uno de estos rangos, también sincronizamos
// endMonth para que el calendario y la consulta queden coherentes entre sí.
// Para agregar un nuevo rango con override (previousMonth, previousQuarter…)
// basta con sumar la entrada — la action no cambia.
//
// `previousYear` solía vivir aquí — lo movimos a `resolveDateRange` para que
// el rango sea self-contained y no se rompa cuando el usuario cambia
// endMonth desde el calendario después de haber elegido "Año Anterior".
const RANGE_OVERRIDES = {};

// Persistencia del rango -------------------------------------------------
// Cambiar de empresa hace window.location.reload() (userAuth/setActiveCompany),
// que reinicia TODO el state Vuex — incluido este módulo. Para que el rango de
// meses elegido sea el MISMO al cambiar de empresa, lo persistimos en
// sessionStorage y sembramos el state al inicializar. sessionStorage (no
// localStorage) es a propósito: sobrevive al reload dentro de la sesión del tab
// pero no arrastra un rango viejo a sesiones futuras (al cerrar el tab vuelve a
// los defaults). `pendingDrill` NO se persiste (es transitorio, cross-tab).
const STORAGE_KEY = "reportsFilters";

function reviveDate(value) {
    return value ? moment(value).toDate() : value;
}

function reviveRange(range) {
    return range && range.from && range.to
        ? { from: reviveDate(range.from), to: reviveDate(range.to) }
        : range;
}

function loadPersisted() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const data = JSON.parse(raw);
        const out = {};
        if (data.endMonth) out.endMonth = reviveDate(data.endMonth);
        if (data.selectedPeriodRange) {
            out.selectedPeriodRange = data.selectedPeriodRange;
        }
        if ("customRange" in data) out.customRange = reviveRange(data.customRange);
        if ("customYearRange" in data) {
            out.customYearRange = reviveRange(data.customYearRange);
        }
        return out;
    } catch (e) {
        return {};
    }
}

function persist(state) {
    try {
        // Date.prototype.toJSON serializa a ISO; reviveDate lo revive al cargar.
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                endMonth: state.endMonth,
                selectedPeriodRange: state.selectedPeriodRange,
                customRange: state.customRange,
                customYearRange: state.customYearRange,
            })
        );
    } catch (e) {
        /* noop — sessionStorage no disponible */
    }
}

const state = () => ({
    endMonth: moment().toDate(),
    selectedPeriodRange: "lastYear",
    // Rango explícito definido por el usuario via AnualCalendar en modo
    // "range" (hoy sólo Ventas). Cuando viene { from, to }, el caller debe
    // setear selectedPeriodRange = "custom" para que `resolveDateRange` lo use
    // en lugar del rango predefinido. Las fechas son Date objects normalizadas
    // al primer día del mes.
    customRange: null,
    // Rango de años explícito elegido en el AnualCalendar (granularidad "year",
    // sólo tabs en modo range). `{ from: Date, to: Date }` normalizado a Ene 1
    // del año. Estado SEPARADO de `customRange` a propósito: la selección de
    // meses (Mensual/Trimestral) y la de años (Anual) no deben pisarse al
    // togglear de período — cada una persiste su propia ventana. `resolveDateRange`
    // sólo lo aplica cuando filterPeriod === "year". null = sin selección
    // (cae al default de "últimas DEFAULT_ANNUAL_YEARS columnas de año").
    customYearRange: null,
    // Drill cross-tab: cuando el usuario clickea una celda del P&L (Resultados)
    // queremos saltar al tab de esa fila y abrir su modal del mes. La URL hace
    // el salto, pero el tab destino monta limpio y no recibe el contexto del
    // click. Este "buzón" guarda { tab, monthLabel }; el tab destino lo consume
    // en su `mounted` (abre el modal) y lo limpia. null = sin drill pendiente.
    pendingDrill: null,
    // Sobrescribe los defaults de arriba con lo persistido (si hay), para que
    // el rango sobreviva al reload del cambio de empresa.
    ...loadPersisted(),
});

const mutations = {
    SET_END_MONTH(state, date) {
        state.endMonth = date;
        persist(state);
    },
    SET_SELECTED_PERIOD_RANGE(state, range) {
        state.selectedPeriodRange = range;
        persist(state);
    },
    SET_CUSTOM_RANGE(state, payload) {
        state.customRange = payload;
        persist(state);
    },
    SET_CUSTOM_YEAR_RANGE(state, payload) {
        state.customYearRange = payload;
        persist(state);
    },
    SET_PENDING_DRILL(state, payload) {
        // Transitorio (buzón cross-tab) — no se persiste.
        state.pendingDrill = payload;
    },
};

const actions = {
    setPeriodRange({ commit }, range) {
        commit("SET_SELECTED_PERIOD_RANGE", range);
        const overrideFn = RANGE_OVERRIDES[range];
        if (overrideFn) commit("SET_END_MONTH", overrideFn());
    },
    // Aplica un rango personalizado: switchea selectedPeriodRange a "custom",
    // sincroniza endMonth al `to` del rango (mantiene la coherencia con el
    // calendar pill y con cualquier consumer que mire endMonth aislado) y
    // guarda el rango. `from`/`to` deben ser Date objects.
    setCustomRange({ commit }, range) {
        if (!range || !range.from || !range.to) return;
        commit("SET_CUSTOM_RANGE", range);
        commit("SET_SELECTED_PERIOD_RANGE", "custom");
        commit("SET_END_MONTH", range.to);
    },
    // Aplica un rango de años (granularidad "year"). A diferencia de
    // `setCustomRange`, NO sincroniza endMonth ni selectedPeriodRange: el span
    // de años es ortogonal al mes de referencia que usan Mensual/Trimestral/YTD,
    // y pisarlos desplazaría sus defaults. `resolveDateRange` lee este estado
    // sólo cuando filterPeriod === "year". `from`/`to` deben ser Date objects.
    setCustomYearRange({ commit }, range) {
        if (!range || !range.from || !range.to) return;
        commit("SET_CUSTOM_YEAR_RANGE", range);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
